/**
 * Pivot state for saved reports.
 *
 * `exportState()` covers every grid feature except pivoting, so a report saved while pivoted
 * would come back flat. We capture the pivot beside the exported blob instead, and the grid
 * puts it back through its controlled `pivotModel` and `pivotActive` props.
 *
 * MUI tracks the gap at https://github.com/mui/mui-x/issues/17295. After a version bump,
 * `npm run test:upstream` says whether this is still needed.
 */

import type { GridPivotModel } from '@mui/x-data-grid-premium';

import type { SummaryReportPivotState } from '../types.ts';

/** Shared and frozen, so the grid never reads a fresh object as a changed model. */
export const EMPTY_PIVOT_MODEL: GridPivotModel = Object.freeze({
    rows: [],
    columns: [],
    values: [],
}) as GridPivotModel;

/** The pivot as the grid has it now, read from the api so a save reflects the screen. */
export function capturePivotState(
    pivoting: { active?: boolean; model?: GridPivotModel } | undefined
): SummaryReportPivotState | undefined {
    if (!pivoting?.model) return undefined;

    return {
        active: pivoting.active ?? false,
        model: {
            rows: pivoting.model.rows ?? [],
            columns: pivoting.model.columns ?? [],
            values: pivoting.model.values ?? [],
        },
    };
}

/** Whether a pivot is worth restoring — an inactive or empty one isn't. */
export function isPivotConfigured(pivotState: SummaryReportPivotState | undefined): boolean {
    if (!pivotState?.active) return false;
    const { rows, columns, values } = pivotState.model;
    return rows.length > 0 || columns.length > 0 || values.length > 0;
}
